<script>
import apiHelper from "@/utils/apiHelpers";
import socket from "@/plugins/socket";
import { mapState } from "vuex";

const getStoredChatSession = () => {
    try {
        const stored = JSON.parse(localStorage.getItem("chatRoomID"));
        if (!stored) return null;
        if (stored.expires && stored.expires < Date.now()) {
            localStorage.removeItem("chatRoomID");
            return null;
        }
        return stored;
    } catch (error) {
        localStorage.removeItem("chatRoomID");
        return null;
    }
};

export default {
    name: "chat-agent-component",
    props: {
        isOpen: {
            type: Boolean,
            required: true,
            default: true,
        },
    },
    data() {
        return {
            newChatMessage: {
                chatRoomID: "",
                message: "",
            },
            startChat: true,
            chatRoomDetails: null,
            step: 1,
            guestName: "",
        };
    },
    computed: {
        ...mapState(["isLoggedIn", "loggedUser"]),
        chatRoomID() {
            if (this.isLoggedIn && this.loggedUser?.chat?.id) {
                return this.loggedUser.chat.id;
            }
            return getStoredChatSession()?.value || null;
        },
    },
    watch: {
        isLoggedIn(newVal) {
            if (!newVal) {
                localStorage.removeItem("chatRoomID");
                this.step = 1;
                this.chatRoomDetails = null;
                this.newChatMessage.chatRoomID = null;
                this.newChatMessage.message = "";
                this.guestName = "";
            } else {
                this.step = 2;
                this.getChatRoomDetails();
            }
        },
        chatRoomID(newVal) {
            console.log("Chat room ID changed to:", newVal);
        },
    },
    methods: {
        appendMessage(message) {
            if (!message?.message) return;
            if (!this.chatRoomDetails) {
                this.chatRoomDetails = { messages: [] };
            }
            if (!Array.isArray(this.chatRoomDetails.messages)) {
                this.chatRoomDetails.messages = [];
            }

            const hasDuplicate = this.chatRoomDetails.messages.some((existingMessage) => {
                if (message.clientTempId && existingMessage.clientTempId) {
                    return existingMessage.clientTempId === message.clientTempId;
                }

                return (
                    existingMessage.chatRoomID === message.chatRoomID &&
                    existingMessage.message === message.message &&
                    existingMessage.is_sender === message.is_sender &&
                    existingMessage.createdAt === message.createdAt
                );
            });

            if (!hasDuplicate) {
                this.chatRoomDetails.messages.push(message);
            }
        },
        handleCloseButton() {
            if (!this.newChatMessage.chatRoomID) {
                this.startChat = false;
            }
            this.$emit("close");
            if (this.isLoggedIn && this.loggedUser?.chat?.id) {
                this.newChatMessage.chatRoomID = this.loggedUser.chat.id;
            }
        },
        async getChatRoomDetails() {
            try {
                if (this.chatRoomID) {
                    const response = await apiHelper.get(
                        `/chats/${this.chatRoomID}`,
                        this.$router,
                    );
                    if (response.success) {
                        const storedSession = getStoredChatSession();
                        const storedMessages = storedSession?.messages || [];
                        this.startChat = false;
                        this.chatRoomDetails = { messages: [] };
                        this.newChatMessage.message = "";
                        const dbMessages = response.chatRoom.messages;
                        const allMessages = [
                            ...storedMessages.map((m) => ({
                                message: m.message,
                                is_sender: m.is_sender,
                                createdAt: m.createdAt,
                            })),
                            ...dbMessages.map((m) => ({
                                message: m.message,
                                is_sender: m.is_sender,
                                createdAt: m.createdAt,
                            })),
                        ];

                        const seen = new Set();
                        const merged = [];

                        for (const msg of allMessages) {
                            const normalized = msg.message.trim().toLowerCase();
                            if (!seen.has(normalized)) {
                                seen.add(normalized);
                                merged.push(msg);
                            }
                        }
                        this.chatRoomDetails.messages = merged;
                        this.step = storedSession?.step || 2;
                        this.guestName = storedSession?.guestName || this.guestName;
                        this.persistChatSession(this.chatRoomID);
                        this.scrollToBottom();
                    } else {
                        throw new Error("Unsuccessful response");
                    }
                }
            } catch (error) {
                console.error("Error getting chat room details:", error);
            }
        },
        async startChatting() {
            this.startChat = false;
            if (!this.chatRoomDetails) {
                this.chatRoomDetails = { messages: [] };
            }

            if (!this.isLoggedIn) {
                const prompt = "For records and assistance, please enter your name.";
                const hasPrompt = this.chatRoomDetails.messages.some(
                    (message) => message.message === prompt,
                );

                if (!hasPrompt) {
                    this.appendMessage({
                        message: prompt,
                        is_sender: false,
                        createdAt: Date.now(),
                    });
                }
                this.step = 1;
                this.persistChatSession();
            } else {
                this.step = 2;
            }
        },
        persistChatSession(chatRoomID = this.newChatMessage.chatRoomID || this.chatRoomID || null) {
            if (this.isLoggedIn) return;

            localStorage.setItem(
                "chatRoomID",
                JSON.stringify({
                    value: chatRoomID,
                    messages: this.chatRoomDetails?.messages || [],
                    step: this.step,
                    guestName: this.guestName,
                    expires: Date.now() + 86400000,
                }),
            );
        },
        async sendMessage() {
            let chatRoomID = this.chatRoomID;
            const input = this.newChatMessage.message.trim();
            if (!input) return;

            // Handle name input for unauthenticated users (Step 1)
            if (!this.isLoggedIn && !chatRoomID && this.step === 1) {
                this.guestName = input.charAt(0).toUpperCase() + input.slice(1);

                // Display name input in chat
                this.appendMessage({
                    message: this.guestName,
                    is_sender: true,
                    createdAt: Date.now(),
                });

                // Create chat room
                const response = await apiHelper.post(
                    "/chats",
                    { message: this.guestName },
                    this.$router,
                );
                if (response.success) {
                    chatRoomID = response.chatRoomID.id;
                    this.newChatMessage.chatRoomID = chatRoomID;

                    // Save the name message to server
                    await apiHelper.post(
                        "/chats/message",
                        {
                            chatRoomID,
                            message: this.guestName,
                        },
                        this.$router,
                    );

                    // Show greeting
                    this.appendMessage({
                        message: `Hi ${this.guestName}! How can I assist you today?`,
                        is_sender: false,
                        createdAt: Date.now(),
                    });

                    this.step = 2;
                    this.newChatMessage.message = "";
                    this.persistChatSession(chatRoomID);
                }
                return;
            }

            // Re-check stored chatRoomID if needed
            if (!chatRoomID) {
                chatRoomID =
                    JSON.parse(localStorage.getItem("chatRoomID"))?.value ||
                    null;
            }

            // Authenticated or ready to send message
            if (chatRoomID) {
                this.newChatMessage.chatRoomID = chatRoomID;
                socket.emit("joinRoom", chatRoomID);

                // Save to backend
                await apiHelper.post(
                    "/chats/message",
                    {
                        chatRoomID,
                        message: input,
                    },
                    this.$router,
                );

                // Push to local message list
                const messageObj = {
                    chatRoomID,
                    message: input,
                    is_sender: true,
                    createdAt: Date.now(),
                    clientTempId: `client-${chatRoomID}-${Date.now()}`,
                };
                this.newChatMessage.message = "";
                this.appendMessage(messageObj);

                // Send via Socket.IO
                socket.emit("sendMessage", messageObj);
                this.persistChatSession(chatRoomID);
                this.scrollToBottom();
            }
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const anchor = this.$refs.chatBottom;
                if (anchor) {
                    anchor.scrollIntoView({ behavior: "auto" });
                }
            });
        },
    },
    created() {
        const storedSession = getStoredChatSession();
        if (!this.isLoggedIn && storedSession?.messages?.length) {
            this.startChat = false;
            this.chatRoomDetails = { messages: storedSession.messages };
            this.step = storedSession.step || (storedSession.value ? 2 : 1);
            this.guestName = storedSession.guestName || "";
            this.newChatMessage.chatRoomID = storedSession.value || null;
        }

        this.getChatRoomDetails();

        socket.on("receiveMessage", (msg) => {
            const roomID =
                this.chatRoomID ||
                JSON.parse(localStorage.getItem("chatRoomID"))?.value ||
                null;
            if (msg.chatRoomID === roomID) {
                console.log("📩 Received message:", msg);
                this.appendMessage(msg);
                this.persistChatSession(roomID);
                this.scrollToBottom();
            } else {
                console.warn(
                    "⚠️ Message ignored — not in this room:",
                    msg.chatRoomID,
                    roomID,
                );
            }
        });

        socket.on("connect", () => {
            const chatRoomID = this.chatRoomID;
            if (chatRoomID) {
                socket.emit("joinRoom", chatRoomID);
                console.log("♻️ Rejoining room after reconnect:", chatRoomID);
            }
        });

        const launcher = document.querySelector(".chat-with-agent");
        const chatBox = document.querySelector(".chat-agent");
        let isDragging = false;
        let offsetX = 0;
        let offsetY = 0;

        launcher?.addEventListener("mousedown", (e) => {
            isDragging = true;
            offsetX = e.clientX - launcher.getBoundingClientRect().left;
            offsetY = e.clientY - launcher.getBoundingClientRect().top;
            document.body.style.userSelect = "none";
        });

        document.addEventListener("mousemove", (e) => {
            if (!isDragging) return;
            const left = e.clientX - offsetX;
            const top = e.clientY - offsetY;

            launcher.style.position = "fixed";
            launcher.style.left = `${left}px`;
            launcher.style.top = `${top}px`;
            launcher.style.right = "auto";
            launcher.style.bottom = "auto";

            if (chatBox && chatBox.style.display !== "none") {
                chatBox.style.position = "fixed";
                chatBox.style.left = `${left}px`;
                chatBox.style.top = `${top - chatBox.offsetHeight - 10}px`;
                chatBox.style.right = "auto";
                chatBox.style.bottom = "auto";
            }
        });

        document.addEventListener("mouseup", () => {
            isDragging = false;
            document.body.style.userSelect = "";
        });
    },
    beforeUnmount() {
        socket.off("receiveMessage");
        socket.off("connect");
    },
};
</script>
